import { Link } from 'react-router-dom';
import { useFinanceDashboard } from '../api/dashboards.ts';
import { Staleness } from '../components/Staleness.tsx';
import { LoadFailure } from '../components/LoadFailure.tsx';
import {
  DashboardFrame, Waiting, formatMoney, listState, useUrlFilters,
} from '../components/DashboardFrame.tsx';
import { ScrollToTop } from '../components/ScrollToTop.tsx';
import { formatDate, parseDateOnly } from '../dates.ts';
import type { ArrangementRow } from '../api/types.ts';


const SORTS: { value: string; label: string }[] = [
  { value: 'name', label: 'Name' },
  { value: '-amount', label: 'Largest first' },
  { value: 'last_billed', label: 'Longest since billed' },
];

/**
 * The finance dashboard: monthly compensation that can be billed this month.
 *
 * An arrangement is here when it is active, has a monthly amount, and has not
 * been billed since the first of the agency's current month. Row security
 * decides whose: a finance case manager sees their own caseload's, accounts
 * payable sees the agency's.
 */
export function FinanceDashboard() {
  return (
    <DashboardFrame
      dashboard="finance"
      title="Collectable this month"
      subtitle="Monthly compensation not yet billed this month, for clients under guardianship now."
    >
      <FinanceBody />
    </DashboardFrame>
  );
}

function FinanceBody() {
  const [params, update] = useUrlFilters();
  const sort = params.get('sort') ?? 'name';

  // Every row, on one scrolling page: no limit is sent.
  const query = useFinanceDashboard({ sort });
  const { failure, takeover } = listState(query);
  const data = takeover ? undefined : query.data;
  const rows = data?.arrangements ?? [];
  const month = data?.billing_month
    ? parseDateOnly(data.billing_month).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
    : null;

  return (
    <>
      <div className="flex flex-wrap items-center gap-3 border-b border-neutral-200 px-4 py-3 lg:px-5">
        <p className="text-[0.9375rem] text-neutral-800 tabular-nums">
          {data ? (
            <>
              <span className="text-2xl font-bold text-neutral-900">{formatMoney(data.collectable_amount)}</span>
              {' '}across {data.collectable.toLocaleString()} arrangement{data.collectable === 1 ? '' : 's'}
              {month && <> for {month}</>}
            </>
          ) : takeover ? 'Not loaded' : 'Adding up…'}
        </p>
        <label htmlFor="finance-sort" className="sr-only">Sort by</label>
        <select
          id="finance-sort"
          value={sort}
          onChange={(e) => update({ sort: e.target.value === 'name' ? null : e.target.value })}
          className="min-h-[44px] rounded-lg border border-neutral-300 bg-white px-2 text-[0.9375rem]
                     text-neutral-800 lg:ml-auto"
        >
          {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>

      {!takeover && (
        <Staleness
          updatedAt={query.dataUpdatedAt}
          isFetching={query.isFetching}
          error={query.error}
          onRefresh={() => void query.refetch()}
        />
      )}

      {failure && takeover ? (
        <LoadFailure
          what="Collectable compensation"
          error={failure}
          onRetry={() => void query.refetch()}
          retrying={query.isFetching}
          forbiddenIsSession={false}
        />
      ) : query.isPending ? (
        <Waiting>Adding up this month’s compensation…</Waiting>
      ) : query.isSuccess && !query.isPlaceholderData && rows.length === 0 ? (
        <Waiting>Nothing left to bill this month.</Waiting>
      ) : (
        <>
          {/* sticky: the columns stay readable however far down */}
          <div className={`sticky top-0 z-10 hidden border-b border-neutral-200 bg-white py-2 pl-5 pr-lane text-xs
                           font-bold tracking-[0.12em] text-neutral-500 uppercase ${COLUMNS} lg:!grid`}>
            <span>Client</span>
            <span>Finance case manager</span>
            <span className="text-right">Monthly</span>
            <span>Last billed</span>
            <span>Pre-paid</span>
          </div>
          {/* room beneath the last row, so the back-to-top button never covers it */}
          <ol className="divide-y divide-neutral-200 pb-20">
            {rows.map((row) => <Row key={row.compensation_id} row={row} />)}
          </ol>
        </>
      )}

      <ScrollToTop />
    </>
  );
}

const COLUMNS =
  'lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,14rem)_8rem_10rem_minmax(0,14rem)] lg:items-center lg:gap-4';

function Row({ row }: { row: ArrangementRow }) {
  const prepaid = [
    row.legal_prepay && `Legal ${formatMoney(row.legal_prepay)}`,
    row.accounting_prepay && `Accounting ${formatMoney(row.accounting_prepay)}`,
  ].filter(Boolean).join(' · ');

  return (
    <li data-quick-client={row.client_id}>
      <Link
        to={`/clients/${row.client_id}`}
        className={`block min-h-[64px] py-3 pl-4 pr-lane hover:bg-neutral-50 active:bg-neutral-100 lg:min-h-[52px]
                    lg:py-2.5 lg:pl-5 ${COLUMNS}`}
      >
        <div className="flex min-w-0 items-baseline gap-2">
          <p className="min-w-0 truncate font-semibold text-neutral-900">{row.display_name}</p>
          <span className="shrink-0 font-mono text-sm text-neutral-500">{row.client_ref}</span>
          <span className="ml-auto font-semibold tabular-nums text-neutral-900 lg:hidden">
            {formatMoney(row.monthly_amount)}
          </span>
        </div>
        <p className="mt-0.5 truncate text-sm text-neutral-600 lg:mt-0 lg:text-[0.9375rem]">
          {row.finance_case_manager ?? 'No finance case manager'}
        </p>
        <p className="hidden text-right font-semibold tabular-nums text-neutral-900 lg:block">
          {formatMoney(row.monthly_amount)}
        </p>
        <p className="text-sm text-neutral-700 tabular-nums lg:text-[0.9375rem]">
          {row.last_billed_date ? `Last billed ${formatDate(row.last_billed_date)}` : 'Never billed'}
        </p>
        <p className="truncate text-sm text-neutral-500">{prepaid}</p>
      </Link>
    </li>
  );
}
